// Owner Errors page (GL-22). The shell route is `errors`.
// An Account Manager is not allowed to open it. The Worker route is Owner-only.

import { AdminApiError, type AdminClient, type OpsEventQuery } from "../api.js";
import { clear, h } from "../dom.js";
import {
  amVisibleDisplayText,
  amVisibleMultilineDisplayText,
} from "../../../ingest-worker/src/am_visible_text.js";
import {
  button,
  disclosure,
  emptyState,
  field,
  focusAfterWrite,
  pageHeader,
  selectField,
} from "../ui/components.js";
import { companyNameText, pluralText, timeAgoText } from "../ui/display-words.js";
import { failureCopy } from "../ui/failure.js";
import { readerRole } from "../ui/facts.js";
import { createFreshnessCaption } from "../ui/freshness.js";

/** Server page size. GET /v1/admin/ops-events ignores `limit` and returns 50. */
export const OPS_ADMIN_PAGE_SIZE = 50;
/** Stop after this many pages so a stuck cursor cannot spin. */
export const OPS_PAGE_CAP = 20;
/**
 * rd R8-17: the Worker pages the log OLDEST first, so reading the whole log
 * until the page cap showed the first 1,000 events of a 180-day log and hid
 * every recent problem. The page asks for a recent window by default; "Show
 * every date" widens it, and the cap banner then says the log has more.
 */
export const PROBLEMS_WINDOW_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;
const STUB_CODE = "not_implemented";
const CODE_RE = /^[a-z][a-z0-9_]{2,63}$/;
const WHERE_RE = /^[A-Za-z0-9_.:]{3,120}$/;
const FINGERPRINT_RE = /^[0-9a-f]{16}$/;
const RUN_PREFIX = "https://github.com/Grand-Vision-Team/";

export interface ErrorsQuery {
  severity: "" | "error" | "warn" | "info";
  companyText: string;
  code: string;
}

export interface ErrorsPageOptions {
  client: AdminClient;
  root: HTMLElement;
  isCurrent?: () => boolean;
  query?: ErrorsQuery;
  now?: Date;
  /** rd R8-17: read every date in the log instead of the recent window. */
  allDates?: boolean;
}

interface ParsedEvent {
  id: string;
  fingerprint: string;
  occurredAt: string;
  source: string;
  severity: string;
  code: string;
  whereAt: string;
  company: string | null;
  requestId: string | null;
  runUrl: string | null;
  message: string | null;
  stack: string | null;
}

export function emptyErrorsQuery(): ErrorsQuery {
  return { severity: "", companyText: "", code: "" };
}

/** Same rules as contracts/fixtures/ops/scrub-vectors.json. Message and stack only. */
export function scrubOpsDisplay(input: string): string {
  let out = "";
  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index);
    if (code <= 31 || code === 127) continue;
    out += input.charAt(index);
  }
  out = out.replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g, "<email>");
  out = out.replace(/Bearer\s+\S+/g, "<redacted>");
  out = out.replace(
    /[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}/g,
    "<redacted>",
  );
  out = out.replace(/\b(?:sk_|pk_|tok_|key_)[A-Za-z0-9]{8,}\b/g, "<redacted>");
  out = out.replace(/\b[0-9a-fA-F]{32,}\b/g, "<redacted>");
  out = out.replace(/-?\d+\.\d+/g, "#.#");
  out = out.replace(/\d{3,}/g, "#");
  return out;
}

export function displayCode(code: string | null | undefined): string {
  if (code === null || code === undefined || code === "" || code === "unknown") {
    return "unknown";
  }
  return CODE_RE.test(code) ? code : "unknown";
}

export function displayRequestId(requestId: string | null | undefined): string {
  if (requestId === null || requestId === undefined) return "unknown";
  const trimmed = requestId.trim();
  if (trimmed === "" || trimmed === "unknown") return "unknown";
  return trimmed;
}

export function displayWhere(whereAt: string | null | undefined): string {
  if (whereAt === null || whereAt === undefined) return "unknown";
  if (!WHERE_RE.test(whereAt) || !whereAt.includes(":")) return "unknown";
  return whereAt;
}

export function issueSearchUrl(fingerprint: string): string | null {
  if (!FINGERPRINT_RE.test(fingerprint)) return null;
  const q = `(${fingerprint}) in:title`;
  return `https://github.com/Grand-Vision-Team/gv-books/issues?q=${encodeURIComponent(q)}`;
}

export function safeRunUrl(url: string | null | undefined): string | null {
  if (url === null || url === undefined || !url.startsWith(RUN_PREFIX)) return null;
  if (url.length > 500) return null;
  return url;
}

function failureDetail(error: unknown): string {
  const requestId = requestIdOf(error);
  // The one home's scrubbed server words. The page is the owner's, and this is
  // a read: failureCopy gives a read its read wording, never a write's.
  const base = failureCopy(error, { role: readerRole(), context: "read" }).detail ?? "Request failed";
  if (requestId === null) return base;
  return `${base}. Request ${requestId}.`;
}

const LOAD_FAILURE_FACE = "We could not load problems. Try again.";

function requestIdOf(error: unknown): string | null {
  if (!(error instanceof AdminApiError)) return null;
  const body = error.body;
  if (body === null || typeof body !== "object") return null;
  const err = (body as { error?: unknown }).error;
  if (err === null || typeof err !== "object") return null;
  const requestId = (err as { requestId?: unknown }).requestId;
  if (typeof requestId !== "string") return null;
  const trimmed = requestId.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function parseEvent(value: unknown): ParsedEvent | null {
  if (value === null || typeof value !== "object") return null;
  const row = value as Record<string, unknown>;
  if (typeof row.id !== "string" || typeof row.fingerprint !== "string") return null;
  if (typeof row.occurred_at !== "string" || typeof row.source !== "string") return null;
  if (typeof row.severity !== "string" || typeof row.code !== "string") return null;
  if (typeof row.where_at !== "string") return null;
  return {
    id: row.id,
    fingerprint: row.fingerprint,
    occurredAt: row.occurred_at,
    source: row.source,
    severity: row.severity,
    code: row.code,
    whereAt: row.where_at,
    company: typeof row.company === "string" ? row.company : null,
    requestId: typeof row.request_id === "string" ? row.request_id : null,
    runUrl: typeof row.run_url === "string" ? row.run_url : null,
    message: typeof row.message === "string" ? row.message : null,
    stack: typeof row.stack === "string" ? row.stack : null,
  };
}

function queryToApi(query: ErrorsQuery): OpsEventQuery {
  const api: OpsEventQuery = {};
  if (query.severity === "error" || query.severity === "warn" || query.severity === "info") {
    api.severity = query.severity;
  }
  const company = query.companyText.trim();
  if (company !== "") api.company = company;
  const code = query.code.trim();
  if (code !== "") api.code = code;
  return api;
}

async function loadEvents(
  client: AdminClient,
  query: ErrorsQuery,
  since: string | null,
): Promise<{ events: ParsedEvent[]; capped: boolean; unreadable: number }> {
  const events: ParsedEvent[] = [];
  let unreadable = 0;
  let cursor: string | undefined;
  const seen = new Set<string>();
  let capped = false;
  for (let page = 0; page < OPS_PAGE_CAP; page += 1) {
    const apiQuery = queryToApi(query);
    // Every page of the walk stays inside the same window, so the cursor and
    // the window cannot disagree.
    if (since !== null) apiQuery.since = since;
    if (cursor !== undefined) apiQuery.cursor = cursor;
    const result = await client.listOpsEvents(apiQuery);
    const rows: unknown[] = Array.isArray(result.events) ? result.events : [];
    for (const row of rows) {
      const parsed = parseEvent(row);
      if (parsed === null) unreadable += 1;
      else events.push(parsed);
    }
    const next = result.next_cursor;
    if (next === null || next === "") {
      capped = false;
      break;
    }
    if (seen.has(next)) {
      throw new Error("Error log cursor did not advance.");
    }
    seen.add(next);
    if (page === OPS_PAGE_CAP - 1) {
      capped = true;
      break;
    }
    cursor = next;
  }
  return { events, capped, unreadable };
}

function visibleEvents(events: readonly ParsedEvent[], query: ErrorsQuery): ParsedEvent[] {
  if (query.code.trim() === STUB_CODE) return [...events];
  return events.filter((event) => event.code !== STUB_CODE);
}

/** Owner list of ops events. Filters are severity, company, and code. */
export async function renderErrorsPage(opts: ErrorsPageOptions): Promise<void> {
  const { client, root } = opts;
  const query = opts.query ?? emptyErrorsQuery();
  const now = opts.now ?? new Date();
  const since = opts.allDates === true ? null : problemsWindowStart(now);
  const stillCurrent = (): boolean => opts.isCurrent?.() ?? true;
  clear(root);
  const page = h("div", { class: "settings-stack" });
  root.append(page);
  const live = h("div", {
    class: "aria-live",
    "aria-live": "polite",
    "data-role": "errors-live",
  });
  page.append(live);
  page.append(h("p", { class: "muted" }, ["Loading problems…"]));

  let loaded: { events: ParsedEvent[]; capped: boolean; unreadable: number };
  try {
    loaded = await loadEvents(client, query, since);
  } catch (error) {
    if (!stillCurrent()) return;
    clear(page);
    page.append(live);
    live.textContent = LOAD_FAILURE_FACE;
    page.append(problemsHeader());
    paintFilters(page, opts, query);
    page.append(problemsWindow(opts));
    const retry = button("Try again", "primary", {
      "data-action": "retry",
      "aria-label": "Retry",
    });
    retry.addEventListener("click", () => {
      void renderErrorsPage(opts);
    });
    page.append(
      h("div", { class: "error-state", role: "alert", "data-role": "errors-load-error" }, [
        h("p", {}, [LOAD_FAILURE_FACE]),
        disclosure("Show details", h("p", { "data-role": "load-error-detail" }, [failureDetail(error)])),
        retry,
      ]),
    );
    return;
  }
  if (!stillCurrent()) return;

  clear(page);
  page.append(live);
  const loadedAt = now;
  page.append(
    createFreshnessCaption({
      updatedAt: loadedAt,
      onRefresh: () => {
        void renderErrorsPage(opts);
      },
    }),
  );
  page.append(problemsHeader());
  paintFilters(page, opts, query);
  page.append(problemsWindow(opts));
  const companyNames = await loadCompanyNames(client);
  if (!stillCurrent()) return;

  const shown = visibleEvents(loaded.events, query);
  const hiddenStubs = loaded.events.length - shown.length;
  if (hiddenStubs > 0) {
    page.append(
      h("div", { "data-role": "stub-hidden" }, [
        h("p", {}, [
          `Hiding ${pluralText(hiddenStubs, "unfinished note", "unfinished notes")}. The filter name is in Show details; apply it there to see those rows.`,
        ]),
        disclosure("Show details", h("p", {}, ["not_implemented"])),
      ]),
    );
  }
  if (loaded.capped) {
    page.append(
      h("p", { class: "banner", "data-role": "page-cap" }, [
        `Showing the first ${pluralText(OPS_PAGE_CAP * OPS_ADMIN_PAGE_SIZE, "event", "events")}. The log has more.`,
      ]),
    );
  }
  if (loaded.unreadable > 0) {
    page.append(
      h("p", { class: "banner banner-err", "data-role": "unreadable" }, [
        `${pluralText(loaded.unreadable, "row could not be read", "rows could not be read")}. Try a narrower filter.`,
      ]),
    );
  }
  if (shown.length === 0) {
    // Round 2 (4): with no filter set, "Nothing matches this filter" blames a
    // filter the person never chose. Say which window was read; the row above
    // carries the "Show every date" pointer.
    const noFilter =
      query.severity === "" && query.companyText.trim() === "" && query.code.trim() === "";
    const emptyWords =
      noFilter && !(opts.allDates === true)
        ? `No problems in the last ${String(PROBLEMS_WINDOW_DAYS)} days.`
        : noFilter
          ? "No problems in the log."
          : "Nothing matches this filter. Clear it or choose another company, then apply again.";
    page.append(emptyState(emptyWords));
    return;
  }

  const table = h("table", { class: "data-table", "data-role": "errors-table" });
  table.append(
    h("thead", {}, [
      h("tr", {}, [
        h("th", {}, ["When"]),
        h("th", {}, ["How serious"]),
        h("th", {}, ["Company"]),
        h("th", {}, ["What happened"]),
      ]),
    ]),
  );
  const tbody = h("tbody", {});
  for (const event of shown) {
    appendEvent(tbody, event, companyNames);
  }
  table.append(tbody);
  page.append(h("div", { class: "table-scroll card" }, [table]));
}

function problemsHeader(): HTMLElement {
  const header = pageHeader(
    "Problems",
    "Problems recorded while the books were updating. Notes are included. Serious items are also filed separately.",
  );
  header.querySelector("h1")?.setAttribute("data-role", "errors-title");
  return header;
}

/** The first instant inside the window the page reads (rd R8-17). */
function problemsWindowStart(now: Date): string {
  return new Date(now.getTime() - PROBLEMS_WINDOW_DAYS * DAY_MS).toISOString();
}

/**
 * The window this list covers, said on screen, with the one press that widens
 * it. A page that reads only part of the log must not look like the whole log.
 */
function problemsWindow(opts: ErrorsPageOptions): HTMLElement {
  const allDates = opts.allDates === true;
  // One constant, so this never needs a plural branch: "the last 7 days".
  const days = "days";
  const row = h("p", { class: "caption", "data-role": "problems-window" });
  // The same quiet control the freshness caption's Refresh is, so a page
  // cannot grow a second style of small button.
  const toggle = h(
    "button",
    {
      type: "button",
      class: "btn btn-ghost",
      "data-action": allDates ? "errors-recent" : "errors-all-dates",
    },
    [allDates ? `Show the last ${String(PROBLEMS_WINDOW_DAYS)} ${days}` : "Show every date"],
  );
  toggle.addEventListener("click", () => {
    void (async (): Promise<void> => {
      try {
        await renderErrorsPage({ ...opts, allDates: !allDates });
        // Round 2 (8): a superseded render must not move focus on the newer one.
        if (!(opts.isCurrent?.() ?? true)) return;
        // The press replaced this control; focus comes back to it.
        focusAfterWrite(
          opts.root.querySelector<HTMLElement>(
            `[data-action="${allDates ? "errors-all-dates" : "errors-recent"}"]`,
          ),
        );
      } catch {
        // The page paints its own failure; the toggle keeps no focus of its own.
      }
    })();
  });
  row.append(
    document.createTextNode(
      allDates
        ? "Showing every date in the log."
        : `Showing the last ${String(PROBLEMS_WINDOW_DAYS)} ${days}.`,
    ),
    document.createTextNode(" "),
    toggle,
  );
  return row;
}

function severityLabel(severity: string): string {
  if (severity === "error") return "Needs attention";
  if (severity === "warn") return "Warning";
  if (severity === "info") return "Note";
  return "Recorded";
}

function formatWhen(iso: string): string {
  const phrase = timeAgoText(iso);
  return phrase === "" ? "Unknown time" : phrase;
}

/**
 * The company column reads as a name. A row with no company is not tied to
 * one; a slug the console does not know is never printed.
 */
function companyFace(
  slug: string | null,
  labels: ReadonlyMap<string, string>,
): string {
  if (slug === null || slug.trim() === "") return "Not tied to a company";
  return companyNameText(slug, labels);
}

/** Company display names, read once per render (AdminClient only). */
async function loadCompanyNames(
  client: AdminClient,
): Promise<ReadonlyMap<string, string>> {
  const names = new Map<string, string>();
  try {
    const { companies } = await client.listCompanies();
    for (const company of companies) {
      const named = company.displayName.trim();
      if (named !== "") names.set(company.slug, named);
    }
  } catch {
    // The column falls back to "this company"; the problems list still reads.
  }
  return names;
}

function paintFilters(root: HTMLElement, opts: ErrorsPageOptions, query: ErrorsQuery): void {
  const severityField = selectField(
    "How serious",
    [
      { value: "", label: "Any" },
      { value: "error", label: "Needs attention" },
      { value: "warn", label: "Warning" },
      { value: "info", label: "Note" },
    ],
    { "data-role": "severity", "aria-label": "Severity" },
  );
  const severity = severityField.querySelector("select");
  if (severity) severity.value = query.severity;
  const companyField = field("Company", {
    "data-role": "company",
    "aria-label": "Company",
    value: query.companyText,
  });
  const codeField = field("Technical name", {
    "data-role": "code",
    "aria-label": "Code",
    value: query.code,
  });
  const apply = button("Apply", "primary", { "data-action": "apply-filters" });
  apply.addEventListener("click", () => {
    const next = readQuery(root);
    void (async () => {
      try {
        await renderErrorsPage({ ...opts, query: next });
        // rd R8-11: the re-render replaced the control that was pressed, so a
        // keyboard user's focus fell to the page body. Put it back on the
        // same control in the new render.
        // `opts.root` is the page's own container: the `root` this handler was
        // built with is the render that the re-render just replaced.
        // Round 3 (3): a superseded render must not move focus on the newer one.
        if (!(opts.isCurrent?.() ?? true)) return;
        focusAfterWrite(opts.root.querySelector<HTMLElement>('[data-action="apply-filters"]'));
      } catch (error) {
        if (!(opts.isCurrent?.() ?? true)) return;
        const region = root.querySelector('[data-role="errors-live"]');
        if (region) region.textContent = LOAD_FAILURE_FACE;
        const existing = root.querySelector('[data-role="load-error-detail"]');
        if (existing) existing.textContent = failureDetail(error);
      }
    })();
  });
  root.append(
    h("div", { class: "form-row card", "data-role": "filters" }, [
      severityField,
      companyField,
      apply,
    ]),
  );
  root.append(
    disclosure(
      "Show details",
      h("div", { class: "form-row", "data-role": "filter-technical-details" }, [
        codeField,
      ]),
    ),
  );
}

function readQuery(root: ParentNode): ErrorsQuery {
  const severityEl = root.querySelector<HTMLSelectElement>('[data-role="severity"]');
  const companyEl = root.querySelector<HTMLInputElement>('[data-role="company"]');
  const codeEl = root.querySelector<HTMLInputElement>('[data-role="code"]');
  const severity = severityEl?.value ?? "";
  const safeSeverity =
    severity === "error" || severity === "warn" || severity === "info" ? severity : "";
  return {
    severity: safeSeverity,
    companyText: companyEl?.value ?? "",
    code: codeEl?.value ?? "",
  };
}

function appendEvent(
  tbody: HTMLTableSectionElement,
  event: ParsedEvent,
  companyNames: ReadonlyMap<string, string>,
): void {
  const issueUrl = event.source === "workflow" ? null : issueSearchUrl(event.fingerprint);
  const runUrl = safeRunUrl(event.runUrl);
  const message = event.message === null ? "unknown" : scrubOpsDisplay(event.message);
  const stack =
    event.stack === null ? null : amVisibleMultilineDisplayText(scrubOpsDisplay(event.stack));
  const shownMessage = amVisibleDisplayText(message);
  const face =
    shownMessage === "unknown" ? "A problem was recorded." : shownMessage;
  const when = formatWhen(event.occurredAt);
  const detailChildren: Array<HTMLElement> = [];
  if (when === "Unknown time") {
    detailChildren.push(h("p", {}, [event.occurredAt]));
  }
  detailChildren.push(
    h("p", { "data-role": "severity" }, [event.severity]),
    h("p", { "data-role": "code" }, [displayCode(event.code)]),
    h("p", { "data-role": "where" }, [displayWhere(event.whereAt)]),
    h("p", { "data-role": "source" }, [event.source]),
    h("p", { "data-role": "request-id" }, [displayRequestId(event.requestId)]),
    h("p", { "data-role": "issue" }, [issueLink(issueUrl, event.source)]),
    h("p", { "data-role": "run" }, [runLink(runUrl)]),
    h("p", { "data-role": "message" }, [shownMessage]),
    stack === null
      ? h("p", { "data-role": "stack" }, ["unknown"])
      : h("pre", { "data-role": "stack" }, [stack]),
  );
  const details = h("div", {}, detailChildren);
  tbody.append(
    h("tr", { "data-fingerprint": event.fingerprint }, [
      h("td", {}, [when]),
      h("td", {}, [severityLabel(event.severity)]),
      h("td", { "data-role": "company" }, [companyFace(event.company, companyNames)]),
      h("td", {}, [h("p", {}, [face]), disclosure("Show details", details)]),
    ]),
  );
}

function issueLink(url: string | null, source: string): Node {
  if (url === null) {
    return document.createTextNode(source === "workflow" ? "workflow issue" : "unknown");
  }
  return h("a", { href: url, "data-role": "issue-link" }, ["Issue"]);
}

function runLink(url: string | null): Node {
  if (url === null) return document.createTextNode("unknown");
  return h("a", { href: url, "data-role": "run-link" }, ["Run"]);
}
